import type { ConfigValue } from '@comficonf/shared';

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

const relativeFormat = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** "5 minutes ago", "yesterday", "just now". */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return 'unknown';
  const seconds = Math.round((time - now) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return 'just now';
  for (const [unit, size] of UNITS) {
    if (abs >= size) return relativeFormat.format(Math.round(seconds / size), unit);
  }
  return relativeFormat.format(Math.round(seconds / 60), 'minute');
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function formatDate(iso: string): string {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? 'unknown' : dateFormat.format(time);
}

export function formatDateTime(iso: string): string {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? 'unknown' : dateTimeFormat.format(time);
}

/** Short display text for a configuration value in change summaries. */
export function formatValue(value: ConfigValue | undefined, maxLength = 80): string {
  if (value === undefined || value === null) return 'not set';
  let text: string;
  if (typeof value === 'string') text = value === '' ? '(empty)' : value;
  else if (typeof value === 'boolean') text = value ? 'On' : 'Off';
  else if (typeof value === 'number') text = String(value);
  else if (Array.isArray(value)) {
    text =
      value.length === 0 ? '(empty list)' : value.map((item) => formatValue(item, 40)).join(', ');
  } else text = JSON.stringify(value);
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}
